<template>
  <form class="space-y-4 rounded-md border border-gray-200 bg-gray-50 p-4" novalidate @submit.prevent="submit">
    <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div class="md:col-span-2">
        <label :for="idPrefix + '-statement'" class="block text-sm font-medium text-gray-700">{{ $t('projects.quiz_statement_label') }}</label>
        <textarea :id="idPrefix + '-statement'" v-model="form.statement" rows="2" required
                  class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-300 sm:text-sm"></textarea>
      </div>
      <div>
        <label :for="idPrefix + '-type'" class="block text-sm font-medium text-gray-700">{{ $t('projects.quiz_question_type_label') }}</label>
        <select :id="idPrefix + '-type'" v-model="form.type" @change="onTypeChange"
                class="mt-1 block w-full px-3 py-2 text-sm text-gray-700 rounded-md bg-white border border-gray-300 focus:border-blue-600 focus:outline-none">
          <option v-for="type in questionTypes" :key="type" :value="type">{{ $t('projects.quiz_type_' + type) }}</option>
        </select>
      </div>
    </div>

    <fieldset>
      <legend class="block text-sm font-medium text-gray-700">{{ $t('projects.quiz_options_label') }}</legend>
      <p class="mt-1 text-xs text-gray-500">{{ $t(form.type === 'single' ? 'projects.quiz_options_help_single' : 'projects.quiz_options_help_multiple') }}</p>

      <div v-for="(option, index) in form.options" :key="option.key"
           class="mt-3 grid grid-cols-1 gap-2 rounded-md bg-white p-3 border md:grid-cols-12 md:items-center"
           :class="option.is_correct ? 'border-green-400' : 'border-gray-200'">
        <label class="inline-flex items-center text-sm text-gray-700 md:col-span-2">
          <input v-if="form.type === 'single'" type="radio" :name="idPrefix + '-correct'" :checked="option.is_correct"
                 @change="markOnlyCorrect(index)" class="h-4 w-4 text-green-600 border-gray-300 focus:ring-bpblue">
          <input v-else v-model="option.is_correct" type="checkbox" class="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-bpblue">
          <span class="ml-2">{{ $t('projects.quiz_correct') }}</span>
        </label>
        <div class="md:col-span-5">
          <label :for="idPrefix + '-option-' + option.key" class="sr-only">{{ $t('projects.quiz_option_text_label', {number: index + 1}) }}</label>
          <input :id="idPrefix + '-option-' + option.key" v-model="option.text" type="text" required
                 :placeholder="$t('projects.quiz_option_text_label', {number: index + 1})"
                 class="block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:border-blue-300 sm:text-sm">
        </div>
        <div class="md:col-span-4">
          <label :for="idPrefix + '-feedback-' + option.key" class="sr-only">{{ $t('projects.quiz_option_feedback_label') }}</label>
          <input :id="idPrefix + '-feedback-' + option.key" v-model="option.feedback" type="text"
                 :placeholder="$t('projects.quiz_option_feedback_label')"
                 class="block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:border-blue-300 sm:text-sm">
        </div>
        <button type="button" v-on:click="removeOption(index)" :disabled="form.options.length <= minOptions"
                :title="$t('projects.quiz_remove_option')" :aria-label="$t('projects.quiz_remove_option')"
                class="justify-self-end p-1 rounded text-red-600 hover:text-red-400 disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-bpblue md:col-span-1">
          <font-awesome-icon icon="times" class="h-4 w-4" aria-hidden="true"/>
        </button>
      </div>

      <button type="button" v-on:click="addOption"
              class="mt-3 inline-flex items-center text-sm font-medium text-bpblue hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-bpblue rounded">
        + {{ $t('projects.quiz_add_option') }}
      </button>
    </fieldset>

    <div>
      <label :for="idPrefix + '-explanation'" class="block text-sm font-medium text-gray-700">{{ $t('projects.quiz_explanation_label') }}</label>
      <textarea :id="idPrefix + '-explanation'" v-model="form.explanation" rows="2" :aria-describedby="idPrefix + '-explanation-help'"
                class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-300 sm:text-sm"></textarea>
      <p :id="idPrefix + '-explanation-help'" class="mt-1 text-xs text-gray-500">{{ $t('projects.quiz_explanation_help') }}</p>
    </div>

    <div class="flex flex-wrap items-center justify-end gap-2">
      <p v-if="error" class="mr-auto text-sm text-red-600" role="alert">{{ $t(error) }}</p>
      <button type="button" v-on:click="$emit('cancel')" :disabled="saving"
              class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
        {{ $t('general.cancel') }}
      </button>
      <button type="submit" :disabled="saving"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-bpblue hover:bg-blue-500 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
        <font-awesome-icon v-if="saving" class="mr-2 h-4 w-4 animate-spin" icon="spinner"/>
        {{ $t('general.save') }}
      </button>
    </div>
  </form>
</template>

<script>
import {MIN_OPTIONS, QUESTION_TYPES, validateQuestion} from "@/views/projects/ProjectEdit/Blocks/Quiz/quizValidation";

let optionCounter = 0
const withKey = (option) => {
  optionCounter += 1
  return {uuid: null, text: '', is_correct: false, feedback: '', ...option, key: option.uuid ?? 'option-' + optionCounter}
}

/*
 * Formulario de una pregunta (nueva o existente). Trabaja sobre una copia, así
 * que cancelar no altera la pregunta original.
 */
export default {
  name: "QuizQuestionForm",
  props: {
    // null para crear una pregunta nueva.
    question: {
      type: Object,
      default: null
    },
    idPrefix: {
      type: String,
      required: true
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  emits: ['save', 'cancel'],
  data() {
    const question = this.question
    return {
      questionTypes: QUESTION_TYPES,
      minOptions: MIN_OPTIONS,
      form: {
        uuid: question?.uuid ?? null,
        type: question?.type ?? 'single',
        statement: question?.statement ?? '',
        explanation: question?.explanation ?? '',
        options: question
            ? question.options.map((option) => withKey({...option}))
            : [withKey({is_correct: true}), withKey({})],
      },
      error: null,
    }
  },
  methods: {
    addOption() {
      this.form.options.push(withKey({}))
    },
    removeOption(index) {
      this.form.options.splice(index, 1)
    },
    markOnlyCorrect(index) {
      this.form.options.forEach((option, optionIndex) => {
        option.is_correct = optionIndex === index
      })
    },
    // Al pasar a opción única se conserva solo la primera respuesta correcta.
    onTypeChange() {
      if (this.form.type !== 'single') return

      const firstCorrect = this.form.options.findIndex((option) => option.is_correct)
      this.markOnlyCorrect(firstCorrect)
    },
    submit() {
      this.error = validateQuestion(this.form)
      if (this.error) return

      this.$emit('save', {
        ...this.form,
        statement: this.form.statement.trim(),
        explanation: this.form.explanation?.trim() ?? '',
        options: this.form.options.map(({uuid, text, is_correct, feedback}) => ({
          uuid,
          text: text.trim(),
          is_correct,
          feedback: feedback?.trim() ?? '',
        })),
      })
    }
  }
}
</script>
